import { ChevronDown, LogOut, RefreshCw, Settings, Shield } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { Me } from "@/lib/api";

type Props = {
  me: Me | null;
  refreshing: boolean;
  settingsLoaded: boolean;
  canRefresh: boolean;
  onRefresh: () => void;
  onOpenSettings: () => void;
  onNavigateAdmin: () => void;
  onLogout: () => void;
};

export function UserMenu({
  me,
  refreshing,
  settingsLoaded,
  canRefresh,
  onRefresh,
  onOpenSettings,
  onNavigateAdmin,
  onLogout,
}: Props) {
  if (!me?.authenticated) return null;

  const refreshDisabled = !canRefresh || refreshing || !settingsLoaded;
  // Chip label falls back through email → org_name → "Account" so an
  // authenticated user without an email claim (older auth tokens, or
  // a mid-rollout drift) still sees a usable trigger instead of the
  // header collapsing to just the theme toggle.
  const chipLabel = me.email || me.org_name || "Account";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 px-2 text-sm text-muted-foreground hover:text-foreground"
          aria-label="Account menu"
        >
          <span className="font-medium text-foreground">{chipLabel}</span>
          {me.app_role && (
            <span
              className="rounded bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary"
              title={`Per-install role: ${me.app_role}`}
            >
              {me.app_role}
            </span>
          )}
          <ChevronDown className="h-4 w-4 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[12rem]">
        {(me.org_name || me.role) && (
          <>
            <DropdownMenuLabel className="font-normal">
              {me.org_name && (
                <div className="text-sm font-medium text-foreground">
                  {me.org_name}
                </div>
              )}
              {me.role && (
                <div className="text-xs text-muted-foreground">
                  org {me.role}
                </div>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem
          onClick={onRefresh}
          disabled={refreshDisabled}
          title="Re-fetch all tickers, bypassing the 4-hour cache"
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          {refreshing ? "Refreshing…" : "Refresh"}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onOpenSettings}>
          <Settings className="mr-2 h-4 w-4" />
          Settings
        </DropdownMenuItem>
        {me.platform_admin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onNavigateAdmin}
              title="Open the admin page"
            >
              <Shield className="mr-2 h-4 w-4" />
              Admin
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
